// Before "Push nach Ange.Bot" goes out: what will be sent, in short, and one more click (operator 02.11.2026). The
// summary is built from the body the API would send now, not from what the deal view shows.
import type { AngeBotChargenvorgabe, AngeBotPosition } from "@pipeline/core";
import type { PushBody } from "../api";
import { esc, fmtSize } from "../util";

const REGTYP: Record<AngeBotPosition["regtyp"], string> = { nem: "NEM", pharma: "Pharma", homoeopathikum: "Homöopathikum", otc: "OTC", unbekannt: "nicht bestimmt" };
const LIEFERFORM: Record<AngeBotPosition["lieferform"], string> = { bulk: "Bulk", abgefuellt: "abgefüllt", unbekannt: "" };
const URTEIL = { machbar: "machbar", unklar: "unklar", nicht_machbar: "nicht machbar" } as const;
const MENGENART = { bestellung: "Bestellung (einmal geliefert)", jahresbedarf: "Jahresbedarf" } as const;
/** How many file names the summary lists before it only counts. */
const FILES_SHOWN = 8;

/** "400–600 kg je Charge, 3 Chargen je Kampagne (Zulassung)" or "keine Vorgabe", as sent. */
export function wireChargenvorgabeText(c: AngeBotChargenvorgabe): string {
  if (!c.vorgegeben) return "keine Vorgabe";
  const { min, max, einheit } = c.je_charge;
  const n = (x: number) => x.toLocaleString("de-DE");
  const size = min !== null && max !== null ? (min === max ? n(min) : `${n(min)}–${n(max)}`) : min !== null ? `ab ${n(min)}` : `bis ${n(max!)}`;
  return `${size} ${einheit} je Charge${c.chargen_je_kampagne ? `, ${c.chargen_je_kampagne} Chargen je Kampagne` : ""} (${c.quelle})`;
}

const fact = (label: string, value: string) => (value.trim() ? `<dt>${label}</dt><dd>${value}</dd>` : "");
const join = (...xs: (string | null | undefined)[]) => xs.filter((x) => x && x.trim()).map((x) => esc(x!)).join(" · ");

function positionHtml(p: AngeBotPosition, i: number): string {
  const subs = p.substanzen.map((s) => esc([s.name_raw, s.dosis, s.einheit].filter(Boolean).join(" "))).join("<br>");
  const menge = [p.menge.value, p.menge.einheit].filter(Boolean).join(" ") + (p.menge.anzahl_chargen ? ` auf ${p.menge.anzahl_chargen} Chargen` : "");
  return `<div class="pc-pos"><b>${i + 1}. ${esc(p.produktname)}</b><dl class="facts">
    ${p.leistung ? fact("Leistung", p.leistung === "nur_verpacken" ? "Nur verpacken" : "Herstellen") : ""}
    ${fact("Darreichungsform", join(p.darreichungsform, p.form_spec))}
    ${fact("Menge", esc(menge))}
    ${p.portion ? fact("Portion je Gebinde", esc(`${p.portion.value} ${p.portion.einheit}`)) : ""}
    ${p.mengenart ? fact("Mengenart", MENGENART[p.mengenart]) : ""}
    ${p.vpe ? fact("VPE", `${p.vpe.toLocaleString("de-DE")} Stück je Verkaufseinheit`) : ""}
    ${fact("Lieferaufteilung", esc(p.menge.lieferstruktur))}
    ${fact(p.substanzen.length === 1 ? "Substanz" : `${p.substanzen.length} Substanzen`, subs)}
    ${fact("Produktart", REGTYP[p.regtyp])}
    ${p.chargenvorgabe ? fact("Chargenvorgabe", esc(wireChargenvorgabeText(p.chargenvorgabe))) : ""}
    ${fact("Lieferform, Verpackung", join(LIEFERFORM[p.lieferform], p.verpackung_roh))}
    ${p.machbarkeit ? fact("Machbarkeit", `${URTEIL[p.machbarkeit.urteil]} <span class="muted">(Urteil von Ange.Bot geht mit)</span>`) : ""}
    ${p.unklarheiten.length ? fact("Hinweise an Ange.Bot", p.unklarheiten.map((u) => esc(u.grund)).join("<br>")) : ""}
  </dl></div>`;
}

/** The summary of what goes to Ange.Bot: company, contact, each position, the files. */
export function pushSummaryHtml(b: PushBody): string {
  const f = b.firma;
  const a = b.ansprechpartner;
  const files = b.anhaenge;
  const shown = files.slice(0, FILES_SHOWN).map((x) => `<li>${esc(x.dateiname)}${x.groesse ? ` <span class="muted">${fmtSize(x.groesse)}</span>` : ""}</li>`);
  return `<p>Das geht als <b>${b.art === "final" ? "finales" : "indikatives"} Angebot</b> an Ange.Bot. Stimmt alles?</p>
    <dl class="facts">
      ${fact("Firma", `${esc(f.name)}${join(f.strasse, [f.plz, f.ort].filter(Boolean).join(" "), f.land, f.domain) ? `<br><span class="muted">${join(f.strasse, [f.plz, f.ort].filter(Boolean).join(" "), f.land, f.domain)}</span>` : ""}`)}
      ${fact("Ansprechpartner", join(a.name, a.email, a.telefon) || `<span class="muted">keiner</span>`)}
      ${fact("Gesendet von", join(b.gesendet_von.name))}
    </dl>
    <h4 class="pc-h">${b.positionen.length === 1 ? "1 Position" : `${b.positionen.length} Positionen`}</h4>
    ${b.positionen.map(positionHtml).join("")}
    <h4 class="pc-h">${files.length === 1 ? "1 Anhang" : `${files.length} Anhänge`}</h4>
    ${files.length ? `<ul class="pc-files">${shown.join("")}${files.length > FILES_SHOWN ? `<li class="muted">und ${files.length - FILES_SHOWN} weitere</li>` : ""}</ul>` : `<p class="muted">Keine.</p>`}`;
}

/** Shows the summary in a dialog; true once "Jetzt pushen" is clicked, false when closed otherwise. */
export function confirmPush(b: PushBody): Promise<boolean> {
  let d = document.querySelector<HTMLDialogElement>("dialog#push-confirm");
  if (!d) {
    d = document.createElement("dialog");
    d.id = "push-confirm";
    d.className = "orig merge pc";
    document.body.append(d);
  }
  const dlg = d;
  dlg.innerHTML = `<div class="orig-h"><div class="orig-meta"><b>Push nach Ange.Bot prüfen</b><span class="mono">${esc(b.ref)}</span></div>
      <button class="xbtn" data-pc-no aria-label="Schließen">×</button></div>
    <div class="merge-b">${pushSummaryHtml(b)}</div>
    <div class="pc-f"><button type="button" class="btn" data-pc-no>Abbrechen</button><button type="button" class="btn primary" data-pc-go>Jetzt pushen</button></div>`;
  return new Promise((resolve) => {
    let go = false;
    dlg.onclick = (e) => {
      const t = e.target as HTMLElement;
      if (t.closest("[data-pc-go]")) {
        go = true;
        dlg.close();
      } else if (t.closest("[data-pc-no]")) dlg.close();
    };
    dlg.onclose = () => resolve(go);
    dlg.showModal();
    dlg.querySelector<HTMLButtonElement>("[data-pc-go]")?.focus();
  });
}
